import clamp from 'lodash/clamp';
import {HTMLAttributes, useRef} from 'react';
import {twMerge} from '../../../common/twMerge';
import {useSeekbarHandler} from '../../../domain/content/[seriesId]/viewer/[productId]/hook/useSeekbarHandler';
import {Text} from '../../atoms/Text/Text';
import {
  INDICATOR_WIDTH_PX,
  PC_VIEWER_SEEKBAR_HEIGHT_PX,
  PC_VIEWER_SEEKBAR_HIDDEN_HEIGHT_PX,
} from './constants';
import {PcViewerSeekbarProps} from './PcViewerSeekbarProps.interface';

export function PcViewerSeekbar({
  loadedProgress,
  currentProgress,
  isProgressBarExpanded,
  currentPage,
  totalPage,
  onMouseEnter,
  onMouseLeave,
  onChangeProgress,
  onPressProgressBar,
  onReleaseProgressBar,
  className,
  ...props
}: PcViewerSeekbarProps) {
  const seekbarRef = useRef<HTMLDivElement>(null);

  const {handlePressSeekbar, handleReleaseSeekbar} = useSeekbarHandler({
    seekbarRef,
    onProgressChange: onChangeProgress,
    onReleaseSeekbar: onReleaseProgressBar,
    onPressSeekbar: onPressProgressBar,
  });

  return (
    <div
      ref={seekbarRef}
      className={twMerge(
        'relative flex w-full cursor-pointer flex-row items-center bg-theme-bg-base',
        className
      )}
      style={{
        height: isProgressBarExpanded
          ? `${PC_VIEWER_SEEKBAR_HEIGHT_PX}px`
          : `${PC_VIEWER_SEEKBAR_HIDDEN_HEIGHT_PX}px`,
      }}
      onMouseDown={handlePressSeekbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={handlePressSeekbar}
      onTouchEnd={handleReleaseSeekbar}
      {...props}
    >
      {/* 인디케이터의 크기만큼 항상 채워서 보여줍니다.
          이 영역은 클릭 시 항상 1페이지로 이동하도록 구현되어야 합니다.*/}
      <div className="relative h-full w-21pxr bg-theme-solid-100" />
      {/* 하단 1px 라인 */}

      <div className="absolute bottom-[-1px] h-[1px] w-full bg-theme-solid-0 opacity-20" />
      <div
        className="relative h-full bg-theme-transparent-10"
        style={{width: `calc(100% - ${INDICATOR_WIDTH_PX}px)`}}
      >
        <div
          className="absolute top-0 left-0 h-full bg-theme-transparent-20"
          style={{width: `${clamp(loadedProgress * 100, 0, 100)}%`}}
        />
        <div
          className={twMerge(
            'relative h-full bg-theme-solid-100',
            isProgressBarExpanded && 'rounded-r-4pxr'
          )}
          style={{width: `${clamp(currentProgress * 100, 0, 100)}%`}}
        >
          {isProgressBarExpanded && (
            <Indicator className="absolute top-0 right-0 rounded-[4px]" />
          )}
        </div>
      </div>
      {isProgressBarExpanded && (
        <div
          className={twMerge('absolute left-1/2 mt-10pxr')}
          style={{
            top: `calc(100% + ${PC_VIEWER_SEEKBAR_HEIGHT_PX}px)`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <PageNumBox currentPage={currentPage} totalPage={totalPage} />
        </div>
      )}
    </div>
  );
}

const Indicator = ({className, ...props}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={twMerge(
        'flex h-12pxr w-21pxr cursor-pointer flex-row items-center justify-center border border-solid border-theme-solid-100 bg-theme-solid-0',
        className
      )}
      {...props}
    >
      <div className="mr-2pxr h-6pxr w-1.5pxr rounded-full bg-theme-solid-100" />
      <div className="h-6pxr w-1.5pxr rounded-full bg-theme-solid-100" />
    </div>
  );
};

const PageNumBox = ({
  currentPage,
  totalPage,
}: {
  currentPage: number;
  totalPage: number;
}) => {
  return (
    <div className="flex w-fit-content flex-row items-center justify-center rounded-4pxr border border-solid border-theme-solid-0 bg-theme-solid-100 py-5pxr px-8pxr">
      {/* 텍스트뷰어나 이미지뷰어는 설계상 로딩중이거나 값이 없으면 0, 이외의 상황에서는 1 ~ count 입니다. */}
      <Text className="font-small1-bold select-none text-right text-theme-solid-10">
        {totalPage && currentPage ? currentPage : '-'}
      </Text>
      <Text className="font-small1 mx-2pxr w-5pxr text-center text-theme-solid-40">
        /
      </Text>
      <Text className="font-small1 select-none text-left text-theme-solid-40">
        {totalPage ? totalPage : '-'}
      </Text>
    </div>
  );
};
